// Toast — globalny systém notifikacii (sek. 53.4 Undo toast, sek. 53.5 dirty potvrdenie)
// API:
//   <ToastProvider>...</ToastProvider>
//   const toast = useToast()
//   toast.info('Hotovo')
//   toast.success('Ulozene', { duration: 3000 })
//   toast.error('Chyba: X')
//   toast.undo('Zmena ulozena', () => revert(), { duration: 10000 })
//
// ARIA role="status" pre screen readery (sek. 54.6).

import React, { createContext, useContext, useState, useCallback, useRef } from 'react'
import { COLORS, RADIUS, TYPO, SPACE, SHADOW, Z_INDEX } from '../theme.js'

const ToastContext = createContext(null)

let _nextId = 1

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])
  const timersRef = useRef({})

  const remove = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id))
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id])
      delete timersRef.current[id]
    }
  }, [])

  const show = useCallback((toast) => {
    const id = _nextId++
    const t = { id, ...toast }
    setToasts(prev => [...prev, t])
    if (t.duration !== 0) {
      timersRef.current[id] = setTimeout(() => remove(id), t.duration || 4000)
    }
    return id
  }, [remove])

  const api = React.useMemo(() => ({
    info:    (msg, opts) => show({ type: 'info',    message: msg, ...opts }),
    success: (msg, opts) => show({ type: 'success', message: msg, ...opts }),
    warn:    (msg, opts) => show({ type: 'warn',    message: msg, ...opts }),
    error:   (msg, opts) => show({ type: 'error',   message: msg, duration: 6000, ...opts }),
    undo:    (msg, onUndo, opts) => show({
      type: 'undo',
      message: msg,
      onUndo,
      duration: (opts && opts.duration) || 10000,
      ...opts
    }),
    dismiss: remove,
    clear:   () => setToasts([])
  }), [show, remove])

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastStack toasts={toasts} onClose={remove} />
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    // Fallback ked je useToast volany mimo Provider — len console
    return {
      info:    (m) => console.log('[toast.info]', m),
      success: (m) => console.log('[toast.success]', m),
      warn:    (m) => console.warn('[toast.warn]', m),
      error:   (m) => console.error('[toast.error]', m),
      undo:    (m) => console.log('[toast.undo]', m),
      dismiss: () => {},
      clear:   () => {}
    }
  }
  return ctx
}

function ToastStack({ toasts, onClose }) {
  if (!toasts.length) return null
  return (
    <div
      role="region"
      aria-label="Notifikacie"
      style={{
        position: 'fixed',
        bottom: SPACE.xl,
        right: SPACE.xl,
        display: 'flex',
        flexDirection: 'column',
        gap: SPACE.sm,
        zIndex: Z_INDEX.toast,
        maxWidth: 420,
        pointerEvents: 'none'
      }}
    >
      {toasts.map(t => <ToastItem key={t.id} toast={t} onClose={() => onClose(t.id)} />)}
    </div>
  )
}

const TYPE_COLORS = {
  info:    { bg: COLORS.info,   border: COLORS.info,   fg: '#fff' },
  success: { bg: COLORS.add,    border: COLORS.add,    fg: '#fff' },
  warn:    { bg: COLORS.warn,   border: COLORS.warn,   fg: '#fff' },
  error:   { bg: COLORS.remove, border: COLORS.remove, fg: '#fff' },
  undo:    { bg: COLORS.text,   border: COLORS.text,   fg: '#fff' }
}

function ToastItem({ toast, onClose }) {
  const c = TYPE_COLORS[toast.type] || TYPE_COLORS.info

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: SPACE.md,
        background: c.bg,
        color: c.fg,
        border: `1px solid ${c.border}`,
        borderRadius: RADIUS.lg,
        padding: '10px 14px',
        boxShadow: SHADOW.lg,
        pointerEvents: 'auto',
        minWidth: 280,
        fontSize: TYPO.sizes.base,
        animation: 'matrix-toast-in 200ms ease-out'
      }}
    >
      <div style={{ flex: 1 }}>{toast.message}</div>
      {toast.type === 'undo' && toast.onUndo && (
        <button
          onClick={() => { toast.onUndo(); onClose() }}
          style={{
            background: 'transparent',
            color: c.fg,
            border: `1px solid ${c.fg}`,
            borderRadius: RADIUS.sm,
            padding: '4px 10px',
            fontWeight: TYPO.weights.bold,
            cursor: 'pointer',
            fontSize: TYPO.sizes.sm,
            fontFamily: 'inherit'
          }}
        >
          ↶ Späť
        </button>
      )}
      <button
        onClick={onClose}
        aria-label="Zavriet"
        style={{
          background: 'transparent',
          color: c.fg,
          border: 'none',
          cursor: 'pointer',
          fontSize: TYPO.sizes.lg,
          padding: 0,
          lineHeight: 1,
          opacity: 0.7
        }}
      >×</button>
    </div>
  )
}

export default ToastProvider
